// src/components/community/CreateThread.js
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import useThreadStore from '../../hooks/useThreadStore';
import MediaSearch from './MediaSearch';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

const CreateThread = ({ onCancel, onSuccess }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('general');
  const [tags, setTags] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showMediaSearch, setShowMediaSearch] = useState(false);
  const [mediaType, setMediaType] = useState('anime');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const isMounted = useRef(true);
  
  const { createThread } = useThreadStore();

  // Set up mounted ref
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Categories for threads
  const categories = [
    { id: 'general', name: 'General' },
    { id: 'anime', name: 'Anime' },
    { id: 'manga', name: 'Manga' },
    { id: 'recommendations', name: 'Recommendations' },
    { id: 'news', name: 'News & Events' },
    { id: 'other', name: 'Other' }
  ];

  // Media type options
  const mediaTypes = [
    { id: 'anime', name: 'Anime' },
    { id: 'manga', name: 'Manga' }
  ];

  // When category changes, auto-set the media type
  useEffect(() => {
    if (category === 'anime' && mediaType !== 'anime') {
      setMediaType('anime');
    } else if (category === 'manga' && mediaType !== 'manga') {
      setMediaType('manga');
    }
  }, [category, mediaType]);

  // Handle media selection
  const handleMediaSelect = (media) => {
    setSelectedMedia(media);
  };

  // Toggle media search visibility
  const toggleMediaSearch = () => {
    setShowMediaSearch(!showMediaSearch);
    if (!showMediaSearch && selectedMedia) {
      // Clear selection when hiding
      setSelectedMedia(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isMounted.current) return;
    
    if (!title.trim() || !content.trim()) {
      setError('Please provide both a title and content for your thread.');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Process tags
      const tagArray = tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
      
      // Create thread data object
      const threadData = {
        title,
        content,
        category,
        tags: tagArray
      };
      
      // Add associated media if selected
      if (selectedMedia) {
        threadData.associatedMediaType = selectedMedia.type;
        threadData.associatedMediaId = selectedMedia.id;
        threadData.associatedMediaTitle = selectedMedia.title;
        threadData.associatedMediaImage = selectedMedia.image;
      }
      
      // Use the createThread function from our hook
      const newThread = await createThread(threadData);
      
      if (newThread && isMounted.current) {
        onSuccess(newThread);
      }
    } catch (err) {
      console.error('Error creating thread:', err);
      if (isMounted.current) {
        setError(err.message || 'Failed to create thread. Please try again.');
      }
    } finally {
      if (isMounted.current) {
        setIsSubmitting(false);
      }
    }
  };

  const handleCancel = () => {
    if (isMounted.current) {
      onCancel();
    }
  };

  return (
    <div className="create-thread">
      <h2 className="section-title">Create New Thread</h2>
      
      {error && <div className="create-thread-error">{error}</div>}
      
      <form className="thread-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="thread-title">Title</label>
          <input
            type="text"
            id="thread-title"
            className="thread-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Give your thread a title..."
            maxLength={200}
            disabled={isSubmitting}
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="thread-category">Category</label>
          <select
            id="thread-category"
            className="thread-category-select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={isSubmitting}
          >
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>
        
        {/* Media association section */}
        <div className="form-group">
          <div className="media-association-header">
            <label>
              Associate with {mediaType === 'anime' ? 'an Anime' : 'a Manga'} 
              <span className="optional"> (Optional)</span>
            </label>
            <button
              type="button"
              className={`toggle-media-btn ${showMediaSearch ? 'active' : ''}`}
              onClick={toggleMediaSearch}
              disabled={isSubmitting}
            >
              {showMediaSearch ? 'Cancel' : 'Link ' + (mediaType === 'anime' ? 'Anime' : 'Manga')}
            </button>
          </div>
          
          {showMediaSearch && (
            <div className="media-search-container">
              <div className="media-type-selector">
                <label htmlFor="media-type" className="media-type-label">Type:</label>
                <select
                  id="media-type"
                  className="media-type-select"
                  value={mediaType}
                  onChange={(e) => setMediaType(e.target.value)}
                  disabled={isSubmitting}
                >
                  {mediaTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>
              
              <MediaSearch 
                onSelect={handleMediaSelect}
                mediaType={mediaType}
                initialValue={null}
              />
            </div>
          )}
        </div>
        
        <div className="form-group">
          <label htmlFor="thread-content">Content</label>
          <textarea
            id="thread-content"
            className="thread-content-textarea"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write your thoughts..."
            rows={10}
            disabled={isSubmitting}
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="thread-tags">
            Tags <span className="optional">(Optional, comma-separated)</span>
          </label>
          <input
            type="text"
            id="thread-tags"
            className="thread-tags-input"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="e.g. shounen, action, recommendations"
            disabled={isSubmitting}
          />
        </div>
        
        <div className="thread-form-actions">
          <button
            type="button"
            className="cancel-thread"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          
          <button
            type="submit"
            className="submit-thread"
            disabled={isSubmitting || !title.trim() || !content.trim()}
          >
            {isSubmitting ? 'Creating...' : 'Create Thread'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateThread;